"use client";

import { useEffect, useRef, useState } from "react";
import { useInvoiceStore } from "@/lib/invoice/store";
import { AI_MODELS, aiConfig, aiStatus, askAboutBusiness, askGemini, onAiStatus } from "@/lib/invoice/ai";
import { Button, Modal, cx } from "./ui";

export const AI_SUGGESTIONS = [
  "Summarise this month in 3 bullet points",
  "Which clients owe me money, and how much?",
  "Which invoices look unusual compared with previous months?",
  "Which clients grew or shrank compared with last month?",
  "Draft a friendly price-increase email for my biggest client",
];

const Sparkle = ({ className = "" }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l1.9 5.6L19.5 9.5l-5.6 1.9L12 17l-1.9-5.6L4.5 9.5l5.6-1.9zM19 14l.9 2.6 2.6.9-2.6.9L19 21l-.9-2.6-2.6-.9 2.6-.9zM5 15l.7 1.8 1.8.7-1.8.7L5 20l-.7-1.8-1.8-.7 1.8-.7z" />
  </svg>
);

// "on" (key saved and the last call worked), "error" (key saved but Gemini failed
// last time) or "off" (no key). Drives the green / red light.
export function useAiState() {
  const store = useInvoiceStore();
  const [st, setSt] = useState(null);
  useEffect(() => {
    setSt(aiStatus());
    return onAiStatus(setSt);
  }, []);
  const cfg = aiConfig(store.settings);
  if (!cfg.enabled) return { state: "off", label: "AI not connected – click to add your Gemini key", model: cfg.model };
  if (st && !st.ok) return { state: "error", label: `AI connection problem: ${st.error}`, error: st.error, model: cfg.model };
  return { state: "on", label: `AI connected (${cfg.model})`, model: cfg.model };
}

export function AiDot({ state, className = "" }) {
  return (
    <span className={cx("relative inline-flex h-2.5 w-2.5", className)} data-testid="ai-dot" data-state={state}>
      {state === "on" && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />}
      <span className={cx("relative inline-flex h-2.5 w-2.5 rounded-full ring-2 ring-white", state === "on" ? "bg-emerald-500" : "bg-red-500")} />
    </span>
  );
}

// Top-bar button – always visible; opens setup first if Gemini isn't connected yet.
export function AskAIButton() {
  const store = useInvoiceStore();
  const [open, setOpen] = useState(false);
  const ai = useAiState();
  if (!store.ready) return null;
  return (
    <>
      <button
        title={ai.label}
        aria-label={`Ask AI – ${ai.label}`}
        onClick={() => setOpen(true)}
        className="group relative hidden h-9 items-center gap-1.5 overflow-hidden rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 px-3.5 text-xs font-semibold text-white shadow-md shadow-fuchsia-600/25 transition hover:shadow-lg hover:shadow-fuchsia-600/40 sm:inline-flex"
        data-testid="ask-ai"
      >
        <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
        <Sparkle className="relative" />
        <span className="relative">Ask AI</span>
        <AiDot state={ai.state} className="relative ml-0.5" />
      </button>
      {open && <AskAIModal onClose={() => setOpen(false)} />}
    </>
  );
}

// Dashboard card with one-click questions.
export function AiInsightsCard() {
  const store = useInvoiceStore();
  const [open, setOpen] = useState(null);
  const on = aiConfig(store.settings).enabled;
  const ai = useAiState();
  return (
    <div className="relative overflow-hidden rounded-2xl p-[1.5px]" data-testid="ai-card" style={{ background: "linear-gradient(120deg,#7c3aed,#db2777,#f97316)" }}>
      <div className="relative h-full rounded-[15px] bg-white p-5">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-fuchsia-200/50 blur-2xl" />
        <div className="relative flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-md shadow-fuchsia-600/30">
            <Sparkle />
          </span>
          <div>
            <div className="flex items-center gap-2 text-[15px] font-semibold tracking-tight text-slate-900">
              AI assistant
              <span className={cx("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset", ai.state === "on" ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-red-50 text-red-700 ring-red-200")} title={ai.label}>
                <AiDot state={ai.state} />
                {ai.state === "on" ? "Connected" : ai.state === "error" ? "Connection problem" : "Not connected"}
              </span>
            </div>
            <div className="text-xs text-slate-500">{on ? "Ask anything about your clients, invoices and sales" : "Powered by Google Gemini – set up in 30 seconds"}</div>
          </div>
        </div>
        <div className="relative mt-4 flex flex-wrap gap-2">
          {AI_SUGGESTIONS.slice(0, 3).map((q) => (
            <button key={q} onClick={() => setOpen(q)} className="rounded-full bg-violet-50 px-3 py-1.5 text-left text-xs font-medium text-violet-800 ring-1 ring-violet-200 transition hover:bg-violet-100">
              {q}
            </button>
          ))}
        </div>
        <button onClick={() => setOpen("")} className="relative mt-4 text-xs font-semibold text-fuchsia-700 hover:underline">
          {on ? "Ask your own question →" : "Connect Gemini →"}
        </button>
      </div>
      {open !== null && <AskAIModal initial={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function AiSetup({ onDone }) {
  const store = useInvoiceStore();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    setBusy(true);
    setErr("");
    const ai = { ...(store.settings.ai || {}), apiKey: key.trim(), model: store.settings.ai?.model || AI_MODELS[0].value, enabled: true };
    try {
      await askGemini({ ...store.settings, ai }, { prompt: "Reply with the single word: connected" });
      store.updateSettings({ ai });
      onDone();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-gradient-to-br from-violet-50 to-fuchsia-50 p-4 ring-1 ring-violet-100">
        <div className="font-semibold text-slate-900">Connect Google Gemini (free)</div>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-700">
          <li>
            Open{" "}
            <a className="font-medium text-violet-700 underline" href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">
              aistudio.google.com/apikey
            </a>{" "}
            and sign in with Google.
          </li>
          <li>Click <b>Create API key</b> and copy it.</li>
          <li>Paste it below.</li>
        </ol>
      </div>
      <input
        autoFocus
        type="password"
        autoComplete="off"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="Paste your Gemini API key (AIza…)"
        className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-violet-500 focus:outline-none focus:ring-4 focus:ring-violet-500/10"
        data-testid="ai-setup-key"
      />
      {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] leading-snug text-slate-500">Only the data needed for each question is sent to Gemini – never bank details or passwords. The key is stored with your (encrypted) data.</p>
        <Button disabled={!key.trim() || busy} onClick={save}>{busy ? "Checking…" : "Connect"}</Button>
      </div>
    </div>
  );
}

function AskAIModal({ onClose, initial = "" }) {
  const store = useInvoiceStore();
  const on = aiConfig(store.settings).enabled;
  const ai = useAiState();
  const [ready, setReady] = useState(on);
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const asked = useRef(false);
  // braces matter: newer Chrome returns a Promise from scrollIntoView, which React
  // would otherwise treat as the effect's cleanup function ("i is not a function")
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [msgs, busy]);

  const ask = async (text) => {
    const question = (text ?? q).trim();
    if (!question || busy) return;
    setQ("");
    const history = msgs.filter((m) => m.role !== "error").slice(-6);
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setBusy(true);
    try {
      const answer = await askAboutBusiness(storeRef.current, question, history);
      setMsgs((m) => [...m, { role: "ai", text: answer }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "error", text: e.message }]);
    } finally {
      setBusy(false);
    }
  };
  const storeRef = useRef(store);
  storeRef.current = store;

  useEffect(() => {
    if (ready && initial && !asked.current) {
      asked.current = true;
      ask(initial);
    }
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><Sparkle className="text-fuchsia-600" /> Ask AI about your business</span>} wide>
      {!ready ? (
        <AiSetup onDone={() => setReady(true)} />
      ) : (
        <div className="flex max-h-[60vh] min-h-[300px] flex-col">
          <div className={cx("mb-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs", ai.state === "on" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800")} data-testid="ai-status">
            <span className="flex min-w-0 items-center gap-2">
              <AiDot state={ai.state} />
              <span className="truncate">{ai.state === "on" ? `Connected to Gemini · ${ai.model}` : ai.error || "Not connected"}</span>
            </span>
            {ai.state !== "on" && (
              <button type="button" className="shrink-0 font-semibold underline" onClick={() => setReady(false)}>
                Change key
              </button>
            )}
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto pr-1">
            {msgs.length === 0 && !busy && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Ask anything about your clients, invoices, projects and sales. Gemini answers from a summary of your data.</p>
                <div className="flex flex-wrap gap-2">
                  {AI_SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => ask(s)} className="rounded-full bg-violet-50 px-3 py-1.5 text-left text-xs font-medium text-violet-800 ring-1 ring-violet-200 hover:bg-violet-100">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={cx("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                <div
                  className={cx(
                    "max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
                    m.role === "user" ? "bg-slate-900 text-white" : m.role === "error" ? "bg-red-50 text-red-800 ring-1 ring-red-200" : "bg-gradient-to-br from-violet-50 to-fuchsia-50 text-slate-800 ring-1 ring-violet-100"
                  )}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex w-fit items-center gap-2 rounded-2xl bg-violet-50 px-4 py-2.5 text-sm text-violet-700">
                <Sparkle className="animate-spin [animation-duration:2s]" /> Thinking…
              </div>
            )}
            <div ref={end} />
          </div>
          <form
            className="mt-3 flex gap-2 border-t border-slate-100 pt-3"
            onSubmit={(e) => {
              e.preventDefault();
              ask();
            }}
          >
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. How much did we bill last quarter?"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-4 focus:ring-violet-500/10"
            />
            <Button type="submit" disabled={busy || !q.trim()}>Ask</Button>
          </form>
        </div>
      )}
    </Modal>
  );
}
